import classNames from 'classnames';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import styles from './video.module.scss';

export interface VideoProps
  extends React.VideoHTMLAttributes<HTMLVideoElement> {
  /** Indicates whether the video should play automatically */
  autoPlay?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether the video should show controls */
  controls?: boolean;
  /** Indicates whether the video is playing */
  isPlaying?: boolean;
  /** Placeholder image url to show until the video loads */
  placeholder?: string;
  /** The source url of the video */
  src?: string;
  /** The format of the video */
  type?: 'video/webm' | 'video/mp4';
}

const Video = forwardRef<any, VideoProps>(
  (
    {
      autoPlay = false,
      className,
      controls = false,
      isPlaying = false,
      placeholder,
      src,
      type = 'video/mp4',
      ...rest
    }: VideoProps,
    ref,
  ) => {
    const componentRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
      if (!componentRef.current) return;

      if (isPlaying) {
        componentRef.current.play();
      } else {
        componentRef.current.pause();
      }
    }, [isPlaying]);

    useImperativeHandle(ref, () => componentRef.current);

    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        {...rest}
        ref={componentRef}
        autoPlay={autoPlay}
        className={classNames(styles.component, className)}
        controls={controls}
        loop={autoPlay}
        muted={autoPlay}
        playsInline={autoPlay}
        poster={placeholder}
      >
        <source src={src} type={type} />

        {`Your browser does not support the video tag.`}
      </video>
    );
  },
);

Video.displayName = 'Video';

export default Video;
